import { useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { newId, unitByCode } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { unitsFor, unitSymbol, useBatches, useLatestVersions, useProducts, useStockLocations, useSuppliers, type ProductRow } from '../../data/catalog.ts'
import { addDays, batchProblems, batchRecordPayload, filterProducts, MAX_EXPIRY_DAYS, MIN_DAY } from '../../data/catalogueUi.ts'
import { buildReceivePayload, locationLabel, previewQuantity, receiveProblem, siteLocations, STOCK_PROBLEM_KEY } from '../../data/stockUi.ts'
import { fmtNum } from '../../lib/format.ts'
import { localDate } from '../../lib/time.ts'
import { PhotoField } from '../../photos/PhotoField.tsx'
import { usePhotoDraft } from '../../photos/usePhotos.ts'
import { BackLink, Banner, DisabledHint, errorText, Field, NotAllowed } from '../../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../../ui/hooks.ts'
import { PICKER_ROWS } from '../WorkOrderParts.tsx'
import { Feedback, Problems, SupplierSelect, useLang, useStockAccess } from './shared.tsx'

/** M15-A, "Réceptionner" (docs/M15.md A): a delivery into the store. The product (searched, or the one the form was opened on), the lot (one of the product's,
 *  or a new one recorded first through batch.record@1), the quantity and its unit with the base preview (the units twin; the server converts again), the place
 *  (the site's default first), a note, and the delivery note's photo (an attachment of kind stock.movement, purpose delivery_note, taken once the receipt is
 *  applied). An online intent under stock.manage (the storeroom has network); the server's verdict shows where the person tapped. */
export function ReceiveForm({ product: fixed, onDone }: { product?: ProductRow; onDone?: () => void }) {
  const { t } = useTranslation()
  const lang = useLang()
  const { site } = useClub()
  const access = useStockAccess()
  const photo = useWriteAccess('attachment.add')
  const w = useWorkshopAction()
  const products = useProducts(); const latest = useLatestVersions(); const batches = useBatches(); const suppliers = useSuppliers(); const locations = useStockLocations()
  const id = useRef({ movement: newId(), batch: newId() })
  const photos = usePhotoDraft()
  const [query, setQuery] = useState('')
  const [productId, setProductId] = useState(fixed?.id ?? '')
  const [quantity, setQuantity] = useState('')
  const [unit, setUnit] = useState('')
  const [locationId, setLocationId] = useState('')
  const [batchId, setBatchId] = useState('')
  const [newLot, setNewLot] = useState(false)
  const [lot, setLot] = useState({ lot: '', expiry: '', received: '' })
  const [supplierId, setSupplierId] = useState('')
  const [note, setNote] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const usable = useMemo(() => (products ?? []).filter((p) => !p.archived_at), [products])
  const matches = useMemo(() => filterProducts(usable, { query, kind: 'all', showArchived: false }), [usable, query])
  const places = useMemo(() => siteLocations(locations ?? [], site.site_id), [locations, site.site_id])
  const product = fixed ?? usable.find((p) => p.id === productId)
  const version = product ? latest?.get(product.id) : undefined
  const units = product ? unitsFor(product.base_unit_code, version?.density_kg_per_l != null) : []
  const chosenUnit = units.some((u) => u.code === unit) ? unit : product?.base_unit_code ?? ''
  const chosenPlace = places.some((l) => l.id === locationId) ? locationId : places.find((l) => l.is_default)?.id ?? places[0]?.id ?? ''
  const lots = (batches ?? []).filter((b) => b.product_id === product?.id)
  const today = localDate(new Date(), site.timezone)
  const preview = product && quantity.trim() ? previewQuantity(quantity, chosenUnit, product, version) : null
  const lotProblems = newLot ? batchProblems(lot, lots.map((b) => b.lot), today) : []
  const problem = receiveProblem({ productId: product?.id ?? '', quantity, unit: chosenUnit, locationId: chosenPlace }, preview)
  const base = product ? unitSymbol(product.base_unit_code, lang) : ''
  if (!access.permitted) return <NotAllowed permission="stock.manage" />
  if (!products || !batches || !suppliers || !locations) return <p>{t('common.loading')}</p>
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!product || problem || lotProblems.length > 0 || !access.canSave) return
    void (async () => {
      let batch = batchId || undefined
      if (newLot) {
        const ok = await w.run('batch.record@1', batchRecordPayload({ batchId: id.current.batch, productId: product.id, lot: lot.lot, expiry: lot.expiry, received: lot.received, supplierId }), 'catalogue.batch.saved', { aggregateId: id.current.batch })
        if (!ok) return
        batch = id.current.batch
      }
      const movementId = id.current.movement
      const ok = await w.run('stock.receive@1', buildReceivePayload({ movementId, productId: product.id, quantity, unit: chosenUnit, locationId: chosenPlace, ...(batch ? { batchId: batch } : {}), note }), 'stock.form.saved', { aggregateId: movementId })
      if (!ok) return
      if (photos.photos.length > 0) await photos.commit({ entity_type: 'stock.movement', entity_id: movementId, purpose: 'delivery_note' })
      setDone(t('stock.form.savedQty', { qty: fmtNum(Number(preview && preview.ok ? preview.qtyBase : 0), lang, 3), unit: base, name: product.name }))
      id.current = { movement: newId(), batch: newId() }
      setQuantity(''); setNote(''); setBatchId(''); setNewLot(false); setLot({ lot: '', expiry: '', received: '' })
      if (!fixed) { setProductId(''); setQuery('') }
      onDone?.()
    })()
  }
  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-3" data-testid="receive-form">
      {!fixed && (
        <>
          <Field label={t('stock.form.search')} htmlFor="rc-search">
            <input id="rc-search" type="search" className="input" autoComplete="off" placeholder={t('parts.searchPlaceholder')} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="receive-search" />
          </Field>
          {query.trim() && (
            <div className="flex flex-col gap-2" role="radiogroup" aria-label={t('stock.form.product')} data-testid="receive-picker">
              {matches.slice(0, PICKER_ROWS).map((p) => (
                <button key={p.id} type="button" role="radio" aria-checked={productId === p.id} className={`btn w-full justify-between ${productId === p.id ? 'btn-primary' : ''}`} onClick={() => { setProductId(p.id); setUnit(''); setBatchId('') }} data-testid="receive-pick" data-product={p.id}>
                  <span className="min-w-0 truncate text-left">{p.name}</span><span className={`shrink-0 text-sm ${productId === p.id ? '' : 'opacity-80'}`}>{unitSymbol(p.base_unit_code, lang)}</span>
                </button>
              ))}
              {matches.length === 0 && <p className="meta" data-testid="receive-no-match">{t('parts.noMatch')}</p>}
              {matches.length > PICKER_ROWS && <p className="meta">{t('parts.more', { count: matches.length - PICKER_ROWS })}</p>}
            </div>
          )}
          <Field label={t('parts.allList')} htmlFor="rc-product">
            <select id="rc-product" className="input" value={productId} onChange={(e) => { setProductId(e.target.value); setUnit(''); setBatchId('') }} data-testid="receive-product">
              <option value="">{t('parts.choose')}</option>
              {usable.map((p) => <option key={p.id} value={p.id}>{p.name} ({unitSymbol(p.base_unit_code, lang)})</option>)}
            </select>
          </Field>
        </>
      )}
      {product && (
        <>
          {!fixed && <p className="font-semibold" data-testid="receive-chosen">{product.name}</p>}
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <Field label={t('stock.form.quantity')} htmlFor="rc-qty">
              <input id="rc-qty" className="input" inputMode="decimal" autoComplete="off" value={quantity} onChange={(e) => { setQuantity(e.target.value); setDone(null) }} data-testid="receive-quantity" />
            </Field>
            <Field label={t('stock.form.unit')} htmlFor="rc-unit">
              <select id="rc-unit" className="input" value={chosenUnit} onChange={(e) => setUnit(e.target.value)} data-testid="receive-unit">
                {units.map((u) => <option key={u.code} value={u.code}>{unitSymbol(u.code, lang)}</option>)}
              </select>
            </Field>
          </div>
          {preview && (preview.ok
            ? (chosenUnit !== product.base_unit_code && <p className="text-sm font-semibold" data-testid="receive-preview">= {fmtNum(Number(preview.qtyBase), lang, 3)} {base}</p>)
            : <p role="alert" className="text-sm font-medium text-red-700 dark:text-red-300" data-testid="receive-preview-error">{preview.code === 'QUANTITY_INVALID' ? t('parts.quantityInvalid') : errorText(t, preview.code)}</p>)}
          {version?.density_kg_per_l == null && (unitByCode(product.base_unit_code)?.dimension === 'mass' || unitByCode(product.base_unit_code)?.dimension === 'volume') && <p className="meta">{t('parts.noDensityHint')}</p>}
          <Field label={t('stock.form.location')} hint={t('stock.form.locationHint')} htmlFor="rc-place">
            <select id="rc-place" className="input" value={chosenPlace} onChange={(e) => setLocationId(e.target.value)} data-testid="receive-location">
              {places.map((l) => <option key={l.id} value={l.id}>{locationLabel(l, lang)}{l.is_default ? ` (${t('stock.form.default')})` : ''}</option>)}
            </select>
          </Field>
          <Field label={t('stock.form.lot')} htmlFor="rc-lot">
            <select id="rc-lot" className="input" value={newLot ? '__new' : batchId} onChange={(e) => { if (e.target.value === '__new') { setNewLot(true); setBatchId('') } else { setNewLot(false); setBatchId(e.target.value) } }} data-testid="receive-batch">
              <option value="">{t('stock.form.noLot')}</option>
              {lots.map((b) => <option key={b.id} value={b.id}>{b.lot}</option>)}
              <option value="__new">{t('stock.form.newLot')}</option>
            </select>
          </Field>
          {newLot && (
            <div className="flex flex-col gap-3 rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" data-testid="receive-new-lot">
              <Field label={t('stock.form.lotNumber')} htmlFor="rc-lot-no"><input id="rc-lot-no" className="input" maxLength={60} value={lot.lot} onChange={(e) => setLot({ ...lot, lot: e.target.value })} data-testid="receive-lot" /></Field>
              <Field label={t('stock.form.expiry')} hint={t('common.optional')} htmlFor="rc-expiry">
                <input id="rc-expiry" type="date" className="input" min={MIN_DAY} max={addDays(today, MAX_EXPIRY_DAYS)} value={lot.expiry} onChange={(e) => setLot({ ...lot, expiry: e.target.value })} data-testid="receive-expiry" />
              </Field>
              <Field label={t('stock.form.receivedOn')} hint={t('common.optional')} htmlFor="rc-received">
                <input id="rc-received" type="date" className="input" min={MIN_DAY} max={today} value={lot.received} onChange={(e) => setLot({ ...lot, received: e.target.value })} data-testid="receive-received" />
              </Field>
              <Field label={t('stock.form.supplier')} htmlFor="rc-supplier"><SupplierSelect id="rc-supplier" value={supplierId} onChange={setSupplierId} suppliers={suppliers} testId="receive-supplier" /></Field>
              <Problems codes={lotProblems} />
            </div>
          )}
          <Field label={t('stock.form.note')} htmlFor="rc-note"><input id="rc-note" className="input" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} data-testid="receive-note" /></Field>
          {photo.permitted && <div className="flex flex-col gap-1"><h3 className="text-sm font-semibold">{t('stock.form.photo')}</h3><PhotoField draft={photos} disabled={!photo.allowed} /></div>}
        </>
      )}
      <Feedback id="receive" error={w.error} pending={w.pending} />
      {done && <Banner tone="ok" testId="receive-done">{done}</Banner>}
      {!access.online && <Banner tone="warn"><span data-testid="receive-offline">{t('stock.form.offlineHint')}</span></Banner>}
      <DisabledHint show={!!problem && access.canSave} testId="receive-hint">{problem ? t(STOCK_PROBLEM_KEY[problem]) : ''}</DisabledHint>
      <button className="btn btn-primary" disabled={w.busy || !!problem || lotProblems.length > 0 || !access.canSave} data-testid="receive-save">{w.busy ? t('common.saving') : t('stock.form.save')}</button>
    </form>
  )
}

/** The page at /catalogue/receive: the form above with the product search, back to the catalogue's Stock tab. */
export function ReceivePage() {
  const { t } = useTranslation()
  return (
    <section className="flex flex-col gap-4" data-testid="receive-page">
      <BackLink to="/catalogue" search={{ tab: 'stock' }}>← {t('stock.title')}</BackLink>
      <h1 className="text-2xl font-bold">{t('stock.receive')}</h1>
      <p className="meta">{t('stock.receiveHint')}</p>
      <ReceiveForm />
    </section>
  )
}
